import { cspNonce } from '@/common/csp'
import { OPEN_HUB_SEARCH, type HubSearchOptions } from '../common/hubSearch'
import { useShutdownRosterAccess } from '../features/leave/hooks/useShutdownRosterAccess'
import { leaveViewPath } from '../features/leave/leaveView'
import { useMyTrainingStatus } from '../hooks/useMyTrainingStatus'
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useHomeNavigationHost } from '../features/home-preview/HomeNavigationSlot'
import HubLinkDirectory from '../features/hub-links/HubLinkDirectory'
import HubGroupNav from '../features/hub-links/HubGroupNav'
import {
  groupHubLinks,
  isHubLinkActive,
  type HubLink,
  type HubLinkEntry,
} from '../features/hub-links/hubLinks'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { HiExclamationTriangle, HiFire, HiMagnifyingGlass, HiUser } from 'react-icons/hi2'
import { REACT_ICONS } from '../config/reactIcons'
import { getAppIconOverride } from '../config/appIcons'
import AdminOverrideModal from './AdminOverrideModal'
import GlobalSearchModal from './GlobalSearchModal'
import { apiClient } from '../common/apiClient'
import { startDemandRefresh } from '../common/demandRefresh'
import { fetchPendingMemosByEmail } from '../services/salesforce'
import CoffeeOrderIndicator from './CoffeeOrderIndicator'
import UserProfileDropdown from './UserProfileDropdown'
import TypingLogo from './TypingLogo'
import { useAuth } from '../contexts/AuthContext'
import { useUserPermissions } from '../contexts/UserPermissionsContext'
import { useHandbookAccess } from '../hooks/handbook/useHandbookAccess'
import { canShowHandbookLink } from '../common/handbookAccess'
import { isHomeWorkspacePath, toHomeWorkspacePath } from '../common/homeWorkspace'
import {
  canBookMeetingRooms,
  DUO_TRAX_LINK,
  hasInterviewNavigation,
  hasManagerNavigation,
  isDuoTraxLink,
} from '../common/navigationRules'
import { isRetiredFeatureLink } from '../common/retiredFeatures'

interface SantaHatSettings {
  enabled: boolean
  x: number
  y: number
  rotation: number
  scale: number
}

// Santa hat that renders inside sidebar for proper z-index stacking
const SidebarSantaHat: React.FC = () => {
  const [settings, setSettings] = useState<SantaHatSettings | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await apiClient.get<SantaHatSettings>('/api/santa-hat/settings')
        setSettings(data)
        // Trigger fade-in after a small delay
        if (data.enabled) {
          setTimeout(() => setIsVisible(true), 100)
        }
      } catch (error) {
        console.error('Error fetching santa hat settings:', error)
      }
    }
    fetchSettings()

    const handleSettingsUpdate = (e: CustomEvent<SantaHatSettings>) => {
      setSettings(e.detail)
      setIsVisible(e.detail.enabled)
    }
    window.addEventListener('santa-hat-settings-updated', handleSettingsUpdate as EventListener)
    return () =>
      window.removeEventListener(
        'santa-hat-settings-updated',
        handleSettingsUpdate as EventListener
      )
  }, [])

  if (!settings || !settings.enabled) return null

  return (
    <div
      className="absolute pointer-events-none hidden lg:block transition-opacity duration-500"
      style={{
        left: settings.x - 40,
        top: settings.y - 18,
        transform: `rotate(${settings.rotation}deg) scale(${settings.scale})`,
        opacity: isVisible ? 1 : 0,
        zIndex: 10,
      }}
    >
      <img
        src="/santa_hat.png"
        alt="Santa Hat"
        style={{ width: 50, height: 'auto' }}
        draggable={false}
      />
    </div>
  )
}

interface TopBarProps {
  userPermissions?: any
  onSelectUser?: (email: string) => void
  enableAdminOverride?: boolean
  userEmail?: string
  onClearOverride?: () => void
}

const TopBar: React.FC<TopBarProps> = ({
  userPermissions = {},
  onSelectUser,
  enableAdminOverride = false,
  userEmail,
  onClearOverride,
}) => {
  const location = useLocation()
  const { allowed: handbookAccess } = useHandbookAccess()
  const isModernHome = location.pathname === '/' || isHomeWorkspacePath(location.pathname)
  const homeNavigationHost = useHomeNavigationHost()
  const { user } = useAuth()
  const { canAccess: canViewShutdown } = useShutdownRosterAccess(user?.email)
  const {
    loggedInUserTeam,
    accessFlags,
    isLoadingAccessFlags,
    isOverride,
    overrideEmail,
    clearOverride,
  } = useUserPermissions()

  // Flag "My Profile" if the current (or viewed) PH staff member still needs to finish
  // their Wise setup (proof of account and/or Wise email).
  const wiseStatusEmail = isOverride ? overrideEmail : undefined
  const { data: wiseStatus } = useQuery<{ outstanding: boolean }>({
    queryKey: ['wise-my-status', wiseStatusEmail ?? 'self'],
    queryFn: () =>
      apiClient.get<{ outstanding: boolean }>(
        `/api/wise-accounts/my-status${wiseStatusEmail ? `?email=${encodeURIComponent(wiseStatusEmail)}` : ''}`
      ),
    staleTime: 5 * 60 * 1000,
  })
  const wiseOutstanding = wiseStatus?.outstanding === true

  const [showAdminModal, setShowAdminModal] = useState(false)
  const [searchOptions, setSearchOptions] = useState<HubSearchOptions>({})
  const [showSearchModal, setShowSearchModal] = useState(false)
  useEffect(() => {
    const open = (event: Event) => {
      setSearchOptions((event as CustomEvent<HubSearchOptions>).detail || {})
      setShowSearchModal(true)
    }
    window.addEventListener(OPEN_HUB_SEARCH, open)
    return () => window.removeEventListener(OPEN_HUB_SEARCH, open)
  }, [])
  // Only System Development can use View As feature
  const canUseViewAs = enableAdminOverride && loggedInUserTeam === 'System Development'
  const [showProfileDropdown, setShowProfileDropdown] = useState(false)
  const [teamNavItems, setTeamNavItems] = useState<any[]>([])
  const [companyServicesNavItems, setCompanyServicesNavItems] = useState<any[]>([])
  const [teamNavigationLoaded, setTeamNavigationLoaded] = useState(false)
  const [servicesNavigationLoaded, setServicesNavigationLoaded] = useState(false)
  const [openTasksCount, setOpenTasksCount] = useState<number>(0)
  const [pendingMemosCount, setPendingMemosCount] = useState<number>(0)

  const [pendingApprovalsCount, setPendingApprovalsCount] = useState<number>(0)
  const [urgentPendingApprovalsCount, setUrgentPendingApprovalsCount] = useState<number>(0)
  const [urgentPayRunReason, setUrgentPayRunReason] = useState<'current' | 'recent' | null>(null)
  const [outstandingKPICount, setOutstandingKPICount] = useState<number>(0)
  const [onboardingCount, setOnboardingCount] = useState<number>(0)
  const [isITSupport, setIsITSupport] = useState<boolean>(false)
  const [pendingDocumentsCount, setPendingDocumentsCount] = useState<number>(0)
  const { outstanding: securityTrainingCount } = useMyTrainingStatus(userEmail)
  const [documentsVisible, setDocumentsVisible] = useState<boolean>(true)
  const [policiesVisible, setPoliciesVisible] = useState<boolean>(false)
  const [pendingPoliciesCount, setPendingPoliciesCount] = useState<number>(0)
  const [systemsOperational, setSystemsOperational] = useState<boolean | null>(null)

  const profileNavItem = {
    path: '/my-profile',
    label: 'My Profile',
    icon: HiUser,
    isReactIcon: true,
  }

  // Cmd/Ctrl-K opens global search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setShowSearchModal(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Open View As modal from ViewAsBanner "Change" button
  useEffect(() => {
    const open = () => setShowAdminModal(true)
    window.addEventListener('open-view-as-modal', open)
    return () => window.removeEventListener('open-view-as-modal', open)
  }, [])

  // Fetch open tasks count
  useEffect(() => {
    const fetchOpenTasksCount = async () => {
      if (!userEmail) {
        return
      }

      try {
        const response = await apiClient.get<{ count: number }>(
          `/api/minutes/tasks/count/open?assignedTo=${encodeURIComponent(userEmail)}`
        )
        setOpenTasksCount(response.count)
      } catch (error) {
        console.error('[Sidebar] Error fetching open tasks count:', error)
      }
    }

    const poll = startDemandRefresh(fetchOpenTasksCount, 5 * 60 * 1000)
    return poll.stop
  }, [userEmail])

  // Fetch pending memos count (supports View As mode)
  useEffect(() => {
    const fetchPendingMemosCount = async () => {
      if (!userEmail) {
        return
      }

      try {
        // Use effective email for View As support
        const effectiveEmail = userEmail
        // Use the same service function as MemoPage for consistency
        const memos = await fetchPendingMemosByEmail(effectiveEmail || userEmail)
        const count = Array.isArray(memos) ? memos.length : 0
        setPendingMemosCount(count)
      } catch (error) {
        console.error('[Sidebar] Error fetching pending memos count:', error)
        // Set to 0 on error to avoid showing stale count
        setPendingMemosCount(0)
      }
    }

    const poll = startDemandRefresh(fetchPendingMemosCount, 5 * 60 * 1000)

    // Listen for memo acceptance events to refresh count immediately
    const handleMemoAccepted = poll.refresh
    window.addEventListener('memo-accepted', handleMemoAccepted)

    return () => {
      poll.stop()
      window.removeEventListener('memo-accepted', handleMemoAccepted)
    }
  }, [userEmail])

  // Fetch pending approvals count (for division leaders)
  useEffect(() => {
    const fetchPendingApprovalsCount = async () => {
      if (!userEmail) {
        return
      }

      try {
        const response = await apiClient.get<{
          success: boolean
          count: number
          urgentCount: number
          payCycle: { priorityReason: 'current' | 'recent' } | null
        }>(`/api/leave/pending-approvals/count?email=${encodeURIComponent(userEmail)}`)
        setPendingApprovalsCount(response?.count || 0)
        setUrgentPendingApprovalsCount(response?.urgentCount || 0)
        setUrgentPayRunReason(response?.payCycle?.priorityReason || null)
      } catch (error) {
        console.error('[Sidebar] Error fetching pending approvals count:', error)
        setPendingApprovalsCount(0)
        setUrgentPendingApprovalsCount(0)
        setUrgentPayRunReason(null)
      }
    }

    const poll = startDemandRefresh(fetchPendingApprovalsCount, 5 * 60 * 1000)

    // Listen for approval changes from LeavePage
    const handleApprovalChange = poll.refresh
    window.addEventListener('leave-approval-changed', handleApprovalChange)

    return () => {
      poll.stop()
      window.removeEventListener('leave-approval-changed', handleApprovalChange)
    }
  }, [userEmail])

  // Fetch outstanding KPI count
  useEffect(() => {
    const fetchOutstandingKPICount = async () => {
      if (!userEmail) {
        return
      }

      try {
        const [response, formsResponse] = await Promise.all([
          apiClient.get<{ success: boolean; data: { kpiFormId?: string; status: string }[] }>(
            '/api/kpi/my-assessments/my-forms'
          ),
          apiClient.get<{ success: boolean; data: { _id: string }[] }>('/api/kpi/forms'),
        ])
        if (response.success && formsResponse.success) {
          // Match the KPI page: assessments with missing/deleted parents are hidden.
          const existingFormIds = new Set((formsResponse.data || []).map((f) => f._id.toString()))
          const inProgressCount = (response.data || []).filter(
            (f) => f.status === 'in_progress' && existingFormIds.has(f.kpiFormId?.toString() || '')
          ).length
          setOutstandingKPICount(inProgressCount)
        }
      } catch (error) {
        console.error('[Sidebar] Error fetching outstanding KPI count:', error)
      }
    }

    const poll = startDemandRefresh(fetchOutstandingKPICount, 5 * 60 * 1000)
    return poll.stop
  }, [userEmail])

  // Fetch pending documents count (supports View As mode)
  useEffect(() => {
    const fetchPendingDocumentsCount = async () => {
      if (!userEmail) return

      try {
        // Use effective email for View As support
        const effectiveEmail = userEmail
        const response = await apiClient.get<any[]>(
          `/api/documents/pending?email=${encodeURIComponent(effectiveEmail || userEmail)}`
        )
        setPendingDocumentsCount(response?.length || 0)
      } catch (error) {
        console.error('[Sidebar] Error fetching pending documents count:', error)
      }
    }

    const poll = startDemandRefresh(fetchPendingDocumentsCount, 5 * 60 * 1000)

    // Listen for document signed events
    const handleDocumentSigned = poll.refresh
    window.addEventListener('document-signed', handleDocumentSigned)

    return () => {
      poll.stop()
      window.removeEventListener('document-signed', handleDocumentSigned)
    }
  }, [userEmail])

  // Check if user is IT Support and fetch onboarding count
  useEffect(() => {
    const checkITSupportAndFetchCount = async () => {
      if (!userEmail) return

      try {
        // Check if user is IT Support staff
        const checkResponse = await apiClient.get<{ isITSupport: boolean }>(
          `/api/onboarding/it-support-staff/check?email=${encodeURIComponent(userEmail)}`
        )
        setIsITSupport(checkResponse.isITSupport)

        // If IT Support, fetch upcoming onboarding count (within 3 days)
        if (checkResponse.isITSupport) {
          const countResponse = await apiClient.get<{ count: number }>(
            '/api/onboarding/upcoming/count?days=3'
          )
          setOnboardingCount(countResponse.count)
        }
      } catch (error) {
        console.error('[Sidebar] Error checking IT support status:', error)
      }
    }

    const poll = startDemandRefresh(checkITSupportAndFetchCount, 5 * 60 * 1000)
    return poll.stop
  }, [userEmail])

  // Fetch system status for System Development sidebar icon
  useEffect(() => {
    if (userPermissions.Team__c !== 'System Development') return
    let gotData = false
    const fetchStatus = async () => {
      gotData = false
      try {
        const res = await apiClient.get<{ apps: { services: { status: string }[] }[] }>(
          '/api/status'
        )
        const appsWithHealth = (res.apps || []).filter((a) => a.services.length > 0)
        if (appsWithHealth.length) {
          gotData = true
          const allOk = appsWithHealth.every((a) =>
            a.services.every((s) => s.status === 'operational')
          )
          setSystemsOperational(allOk)
        }
      } catch {
        /* keep null until we get a real response */
      }
    }
    // Retry only on demand; events force an immediate refresh after changes.
    const poll = startDemandRefresh(fetchStatus, () => (gotData ? 5 * 60 * 1000 : 15_000))
    const onStatusUpdated = poll.refresh
    window.addEventListener('status-updated', onStatusUpdated)
    return () => {
      poll.stop()
      window.removeEventListener('status-updated', onStatusUpdated)
    }
  }, [userPermissions.Team__c])

  // Check Documents sidebar visibility (supports View As mode)
  useEffect(() => {
    const checkDocumentsVisibility = async () => {
      if (!userEmail) return

      try {
        // Use effective email for View As support
        const effectiveEmail = userEmail
        const response = await apiClient.get<{ visible: boolean }>(
          `/api/documents/sidebar/visibility?email=${encodeURIComponent(effectiveEmail || userEmail)}`
        )
        setDocumentsVisible(response.visible)
      } catch (error) {
        console.error('[Sidebar] Error checking Documents visibility:', error)
        setDocumentsVisible(true) // Default to visible on error
      }
    }

    checkDocumentsVisibility()
  }, [userEmail])

  // Check Policies sidebar visibility (HR/admins, or new starters with assigned policies)
  useEffect(() => {
    const checkPoliciesVisibility = async () => {
      if (!userEmail) return
      try {
        const response = await apiClient.get<{ visible: boolean; pendingCount: number }>(
          `/api/policies/sidebar/visibility?email=${encodeURIComponent(userEmail)}`
        )
        setPoliciesVisible(response.visible)
        setPendingPoliciesCount(response.pendingCount || 0)
      } catch (error) {
        console.error('[Sidebar] Error checking Policies visibility:', error)
        setPoliciesVisible(false)
      }
    }

    checkPoliciesVisibility()
    const onAck = () => checkPoliciesVisibility()
    window.addEventListener('policy-acknowledged', onAck)
    return () => window.removeEventListener('policy-acknowledged', onAck)
  }, [userEmail])

  // Fetch team apps when userPermissions changes
  useEffect(() => {
    const fetchTeamApps = async () => {
      if (!userPermissions.Team__c) {
        setTeamNavItems([])
        setTeamNavigationLoaded(true)
        return
      }

      setTeamNavigationLoaded(false)
      try {
        const response = await apiClient.get<any[]>(
          `/api/team-apps/team/${encodeURIComponent(userPermissions.Team__c)}`
        )
        const data = response || []

        // Get user's company and location from permissions
        const userCompany = userPermissions.Company__c
        const userLocation = userPermissions.Division_Location__c

        // Filter apps based on company, location, and visibility
        const filteredData = data.filter((app: any) => {
          if (isRetiredFeatureLink(app.app_url)) return false
          // Check visibility - hide inactive apps
          if (app.visibility === 'inactive') return false

          // Hide admin_only apps from non-admin users
          if (app.visibility === 'admin_only' && userPermissions.Team__c !== 'System Development') {
            return false
          }

          // Check company filter
          const companyMatch = app.company === 'All' || (userCompany && app.company === userCompany)
          if (!companyMatch) return false

          // Check location filter
          const locationMatch =
            app.location === 'All' || (userLocation && app.location === userLocation)
          if (!locationMatch) return false

          return true
        })

        // Transform team apps to nav item format
        const navItems = filteredData.map((app: any) => {
          const iconOverride = getAppIconOverride(app.app_name, app.app_url)
          const IconComp = iconOverride ? undefined : REACT_ICONS[app.icon_path]

          return {
            path: app.app_url,
            label: app.app_name,
            icon: iconOverride || IconComp || app.icon_path || '/dt_favicon.png',
            isReactIcon: !!IconComp,
            external: app.app_url.startsWith('http'),
            visibility: app.visibility,
          }
        })

        setTeamNavItems(navItems)
      } catch (error) {
        console.error('Error fetching team apps:', error)
      } finally {
        setTeamNavigationLoaded(true)
      }
    }

    fetchTeamApps()
  }, [userPermissions.Team__c, userPermissions.Company__c, userPermissions.Division_Location__c])

  // Fetch company services
  useEffect(() => {
    const fetchCompanyServices = async () => {
      // Only fetch if user is authenticated (has an Email)
      if (!userPermissions.Email) {
        setCompanyServicesNavItems([])
        setServicesNavigationLoaded(true)
        return
      }

      setServicesNavigationLoaded(false)
      try {
        const response = await apiClient.get<any[]>('/api/company-services')
        const data = response || []

        // Get user's company and location from permissions
        const userCompany = userPermissions.Company__c
        const userLocation = userPermissions.Division_Location__c

        // Filter services based on company, location, and visibility
        const filteredData = data.filter((service: any) => {
          if (isRetiredFeatureLink(service.app_url)) return false
          // Check visibility - hide inactive services
          if (service.visibility === 'inactive') return false

          // Hide admin_only services from non-admin users
          if (
            service.visibility === 'admin_only' &&
            userPermissions.Team__c !== 'System Development'
          ) {
            return false
          }

          // Hide "Company Directory" from non-division leaders
          const isDivisionLeader =
            userPermissions.Division_Leader__c === 'true' ||
            userPermissions.Division_Leader__c === true
          if (
            (service.app_name === 'Company Directory' || service.app_url?.includes('/directory')) &&
            !isDivisionLeader
          ) {
            return false
          }

          // Check company filter
          const companyMatch =
            service.company === 'All' || (userCompany && service.company === userCompany)
          if (!companyMatch) return false

          // Check location filter
          const locationMatch =
            service.location === 'All' || (userLocation && service.location === userLocation)
          if (!locationMatch) return false

          return true
        })

        // Transform company services to nav item format with category info
        const navItems = filteredData.map((service: any) => {
          const iconOverride = getAppIconOverride(service.app_name, service.app_url)
          const IconComp = iconOverride ? undefined : REACT_ICONS[service.icon_path]

          return {
            path: service.app_url,
            label: service.app_name,
            icon: iconOverride || IconComp || service.icon_path || '/dt_favicon.png',
            isReactIcon: !!IconComp,
            external: service.app_url.startsWith('http'),
            visibility: service.visibility,
            categoryName: service.category_name || null,
            categorySortOrder: service.category_sort_order ?? 999999,
          }
        })

        setCompanyServicesNavItems(navItems)
      } catch (error) {
        console.error('Error fetching company services:', error)
      } finally {
        setServicesNavigationLoaded(true)
      }
    }

    fetchCompanyServices()
  }, [
    userPermissions.Email,
    userPermissions.Company__c,
    userPermissions.Division_Location__c,
    userPermissions.Team__c,
  ])

  // Check if user is System Development team member
  const isSystemDev = userPermissions.Team__c === 'System Development'
  const showSettings = isSystemDev

  // Counts match on the route as well as the label, so renamed grouped links keep them.
  const renderBadges = (item: { path: string; label: string }) => {
    const path = item.path.split(/[?#]/)[0]
    const is = (paths: string[], labels: string[]) =>
      paths.includes(path) || labels.includes(item.label)
    const countClass =
      'inline-flex items-center justify-center h-5 w-5 rounded-full text-white text-[11px] font-bold shadow-lg'
    const badges = [
      is(['/my-profile'], ['My Profile']) && wiseOutstanding && (
        <span
          key="wise"
          title="Wise account setup outstanding"
          className={`${countClass} bg-red-500`}
        >
          !
        </span>
      ),
      is(['/tasks', '/my-tasks'], ['My Tasks']) && openTasksCount > 0 && (
        <span key="tasks" className={`${countClass} bg-orange-500`}>
          {openTasksCount}
        </span>
      ),
      (path === '/memo' ||
        item.label === 'Memos' ||
        item.label === 'Company Memos' ||
        item.label?.toLowerCase().includes('memo')) &&
        pendingMemosCount > 0 && (
          <span key="memos" className={`${countClass} bg-orange-500`}>
            {pendingMemosCount}
          </span>
        ),
      is(['/cafe'], ['Duo Cafe']) && userEmail && (
        <CoffeeOrderIndicator key="cafe" userEmail={userEmail} />
      ),
      is(['/leave'], ['Leave', "My Team's Leave"]) && pendingApprovalsCount > 0 && (
        <span
          key="leave"
          title={
            urgentPendingApprovalsCount > 0
              ? urgentPayRunReason === 'recent'
                ? `${urgentPendingApprovalsCount} leave approval${urgentPendingApprovalsCount === 1 ? '' : 's'} need follow-up from the recent pay run`
                : `${urgentPendingApprovalsCount} leave approval${urgentPendingApprovalsCount === 1 ? '' : 's'} affect the current pay run`
              : `${pendingApprovalsCount} pending leave approval${pendingApprovalsCount === 1 ? '' : 's'}`
          }
          className={`inline-flex h-5 min-w-5 items-center justify-center gap-0.5 rounded-full px-1 text-[11px] font-bold text-white shadow-lg ${
            urgentPendingApprovalsCount > 0 ? 'bg-red-600' : 'bg-orange-500'
          }`}
        >
          {urgentPendingApprovalsCount > 0 && (
            <HiExclamationTriangle className="h-3 w-3" aria-hidden="true" />
          )}
          {pendingApprovalsCount}
        </span>
      ),
      is(['/kpi'], ['My KPI', 'KPI']) && outstandingKPICount > 0 && (
        <span key="kpi" className={`${countClass} bg-orange-500`}>
          {outstandingKPICount}
        </span>
      ),
      is(['/system-development'], ['System Development']) && isITSupport && onboardingCount > 0 && (
        <span key="onboarding" className={`${countClass} bg-orange-500`}>
          {onboardingCount}
        </span>
      ),
      is(['/documents'], ['Documents']) && pendingDocumentsCount > 0 && (
        <span key="documents" className={`${countClass} bg-orange-500`}>
          {pendingDocumentsCount}
        </span>
      ),
      is(['/policies'], ['Policies']) && pendingPoliciesCount > 0 && (
        <span key="policies" className={`${countClass} bg-orange-500`}>
          {pendingPoliciesCount}
        </span>
      ),
      is(['/security-training'], ['Security Training']) && securityTrainingCount > 0 && (
        <span key="training" className={`${countClass} bg-red-500`}>
          {securityTrainingCount}
        </span>
      ),
    ].filter(Boolean)
    return badges.length ? <span className="ml-auto flex items-center gap-2">{badges}</span> : null
  }

  const flashMailNavItem = companyServicesNavItems.find(
    (item: any) =>
      canShowHandbookLink(item.path, handbookAccess) &&
      item.label?.trim().toLowerCase() === 'flash mail'
  )
  const haircutsNavItem = teamNavItems.find(
    (item: any) => item.path === '/haircuts' || item.label?.trim().toLowerCase() === 'haircuts'
  )
  const showMassages = Boolean(haircutsNavItem) && userPermissions.Division_Location__c === 'Sydney'
  // Only line managers and seating coordinators see the plan; the API decides.
  const seatingAccess = useQuery({
    queryKey: ['parra-seating-access', wiseStatusEmail ?? 'self'],
    queryFn: () => apiClient.get<{ canView: boolean }>('/api/parra-seating/access'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const showSeatingPlan = seatingAccess.data?.canView === true
  const seatingPlanNavItem = {
    path: '/parra-seating-plan',
    label: 'Parramatta Seating',
    icon: REACT_ICONS['HiMapPin'],
    isReactIcon: true,
  }
  const massagesNavItem = {
    path: '/massages',
    label: 'Massages',
    icon: REACT_ICONS['HiSparkles'],
    isReactIcon: true,
  }

  const filteredTeamApps = teamNavItems.filter((item: any) => {
    if (!canShowHandbookLink(item.path, handbookAccess)) return false
    if (
      item.path === '/haircuts' ||
      item.path === '/massages' ||
      item.label?.trim().toLowerCase() === 'haircuts' ||
      item.label?.trim().toLowerCase() === 'massages'
    ) {
      return false
    }
    if (item.path === '/bills' && !accessFlags.bills) return false
    if (item.path === '/documents' && !documentsVisible) return false
    return true
  })

  const isLineManager = hasManagerNavigation(userPermissions, userEmail)
  const hasInterviews = hasInterviewNavigation(userPermissions, userEmail)
  const showRoomBookings = canBookMeetingRooms(userPermissions)
  const alreadyHasDuoTrax = filteredTeamApps.some((item: any) =>
    isDuoTraxLink(item.path, item.label)
  )
  const showDuoTrax = isLineManager && !alreadyHasDuoTrax
  const navigationReady = teamNavigationLoaded && servicesNavigationLoaded && !isLoadingAccessFlags
  const teamItemsForNavigation = [
    ...(canViewShutdown
      ? [
          {
            path: leaveViewPath('shutdown'),
            label: 'Skeleton Crew',
            icon: REACT_ICONS['HiCalendarDays'],
            isReactIcon: true,
          },
        ]
      : []),
    ...(flashMailNavItem ? [flashMailNavItem] : []),
    ...filteredTeamApps,
    ...(showDuoTrax
      ? [
          {
            ...DUO_TRAX_LINK,
            icon: getAppIconOverride('duo trax') || '/dt_favicon.png',
            external: true,
          },
        ]
      : []),
    ...(isLineManager
      ? [
          {
            path: '/staff_actions_data',
            label: 'Staff Actions Data',
            icon: REACT_ICONS['HiClipboardList'],
            isReactIcon: true,
          },
        ]
      : []),
    ...(!isLoadingAccessFlags && accessFlags.dqsAdmin
      ? [
          {
            path: '/dqs',
            label: 'DQS Documents',
            icon: REACT_ICONS['HiFolder'],
            isReactIcon: true,
          },
          {
            path: '/sharepoint-access',
            label: 'SharePoint Access',
            icon: REACT_ICONS['HiShieldCheck'],
            isReactIcon: true,
          },
        ]
      : []),
    ...(!accessFlags.systemDev && accessFlags.duoServices
      ? [
          {
            path: '/duo-services',
            label: 'Duo Services',
            icon: REACT_ICONS['HiWrenchScrewdriver'],
            isReactIcon: true,
          },
        ]
      : []),
    ...(!isLoadingAccessFlags && accessFlags.wellbeingAllowance
      ? [
          {
            path: '/wellbeing-allowance',
            label: 'Wellbeing Allowance',
            icon: REACT_ICONS['HiOutlineHeart'],
            isReactIcon: true,
          },
        ]
      : []),
  ]
  const filteredServices = companyServicesNavItems.filter((item: any) => {
    if (!canShowHandbookLink(item.path, handbookAccess)) return false
    if (item.label?.trim().toLowerCase() === 'flash mail') return false
    if (item.path === '/bills' && !accessFlags.bills) return false
    if (item.path === '/documents' && !documentsVisible) return false
    return true
  })
  const servicesForNavigation = [
    ...filteredServices,
    ...(hasInterviews && !filteredServices.some((item: any) => item.path === '/interviews')
      ? [
          {
            path: '/interviews',
            label: 'Interviews',
            icon: REACT_ICONS['HiUserGroup'],
            isReactIcon: true,
            categoryName: null,
          },
        ]
      : []),
    ...(isLineManager && !filteredServices.some((item: any) => item.path === '/uniforms')
      ? [
          {
            path: '/uniforms',
            label: 'Team Uniforms',
            icon: REACT_ICONS['HiUserGroup'],
            isReactIcon: true,
            categoryName: null,
          },
        ]
      : []),
  ]
  const groupedServices = servicesForNavigation.reduce(
    (acc: Record<string, any[]>, item: any) => {
      const key = item.categoryName || '__default__'
      if (!acc[key]) acc[key] = []
      acc[key].push(item)
      return acc
    },
    {} as Record<string, any[]>
  )
  const serviceCategoryPriority: Record<string, number> = {
    people: 0,
    __default__: 1,
    submissions: 2,
    training: 3,
  }
  const serviceCategoryKeys = Object.keys(groupedServices).sort((a, b) => {
    const aPriority = serviceCategoryPriority[a.toLowerCase()] ?? 999999
    const bPriority = serviceCategoryPriority[b.toLowerCase()] ?? 999999
    if (aPriority !== bPriority) return aPriority - bPriority
    return (
      (groupedServices[a][0]?.categorySortOrder ?? 999999) -
      (groupedServices[b][0]?.categorySortOrder ?? 999999)
    )
  })
  // The default section always has the seating plan, so it is always present.
  if (!serviceCategoryKeys.includes('__default__')) {
    serviceCategoryKeys.splice(Math.min(1, serviceCategoryKeys.length), 0, '__default__')
  }
  const getExpandedServiceCategoryItems = (categoryKey: string) => {
    const items: any[] = []

    if (categoryKey.toLowerCase() === 'people') items.push(profileNavItem)
    if (categoryKey === '__default__' && policiesVisible) {
      items.push({
        path: '/policies',
        label: 'Policies',
        icon: REACT_ICONS['HiBookOpen'],
        isReactIcon: true,
      })
    }
    if (categoryKey === '__default__' && haircutsNavItem) items.push(haircutsNavItem)
    if (categoryKey === '__default__' && showMassages) items.push(massagesNavItem)
    if (categoryKey === '__default__' && showSeatingPlan) items.push(seatingPlanNavItem)

    for (const item of groupedServices[categoryKey] || []) {
      items.push(item)
      if (item.path === '/minutes' && showRoomBookings) {
        items.push({
          path: '/meeting-room',
          label: 'Room Bookings',
          icon: REACT_ICONS['HiBuildingOffice2'],
          isReactIcon: true,
        })
      }
      if (item.label === 'Einstein Idea') {
        items.push({
          path: '/case-studies',
          label: 'Case Studies',
          icon: REACT_ICONS['HiBookOpen'],
          isReactIcon: true,
        })
      }
    }

    return items
  }

  // The same permission-filtered links as the hover menus and My links, in one list.
  const hubLinkSections = groupHubLinks([
    profileNavItem,
    ...serviceCategoryKeys.flatMap((categoryKey) =>
      getExpandedServiceCategoryItems(categoryKey).map((item): HubLink => ({
        ...item,
        source: 'service',
      }))
    ),
    ...teamItemsForNavigation.map((item: any): HubLink => ({
      ...item,
      source: 'team',
      to:
        item.label === 'System Development' && systemsOperational === false
          ? '/system-development/status'
          : undefined,
    })),
  ])
  // Required actions mark their group; open tasks alone do not (they are being phased out).
  const linkNeedsAttention = (link: HubLinkEntry) => {
    const path = link.path.split(/[?#]/)[0]
    return (
      (path === '/my-profile' && wiseOutstanding) ||
      (path === '/memo' && pendingMemosCount > 0) ||
      (path === '/documents' && pendingDocumentsCount > 0) ||
      (path === '/policies' && pendingPoliciesCount > 0) ||
      (path === '/security-training' && securityTrainingCount > 0) ||
      (path === '/kpi' && outstandingKPICount > 0) ||
      (path === '/leave' && pendingApprovalsCount > 0)
    )
  }
  const isHubActive = (link: HubLinkEntry) => {
    if (isHubLinkActive(link, location.pathname, location.search)) return true
    // Leave and IT help open as panels over the home page.
    const destination = link.to.split(/[?#]/)[0]
    const workspacePath = link.external ? null : toHomeWorkspacePath(destination)
    return Boolean(
      workspacePath &&
      workspacePath !== destination &&
      location.pathname === workspacePath &&
      isHubLinkActive(link, destination, location.search)
    )
  }
  const renderHubLinkIcon = (link: HubLinkEntry) => {
    if (link.path !== '/system-development') return undefined
    if (systemsOperational === false)
      return <HiFire className="text-red-500 animate-[fireGlowWobble_1s_ease-in-out_infinite]" />
    return (
      <span
        className={`h-2.5 w-2.5 rounded-full ${systemsOperational ? 'bg-green-500' : 'bg-gray-300'}`}
        title={systemsOperational ? 'All systems operational' : 'Checking system status'}
      />
    )
  }
  const hubDirectoryProps = {
    sections: hubLinkSections,
    loading: !navigationReady,
    renderBadge: renderBadges,
    renderIcon: renderHubLinkIcon,
    isActive: isHubActive,
    needsAttention: linkNeedsAttention,
  }

  const menuTriggerClass =
    'flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-semibold text-gray-600 outline-none transition-colors hover:bg-gray-100 hover:text-gray-900 data-[state=open]:bg-gray-100 data-[state=open]:text-gray-900'

  return (
    <header
      data-top-bar
      className={`relative z-40 shrink-0 ${isModernHome ? 'bg-[#f5f6fc]' : 'bg-transparent'}`}
    >
      <div className="relative flex h-12 min-w-0 items-center gap-1 border-b border-gray-200 bg-white px-3">
        <div className="relative flex h-12 shrink-0 items-center">
          <TypingLogo />
          <SidebarSantaHat />
        </div>
        <HubGroupNav
          {...hubDirectoryProps}
          triggerClassName={`${menuTriggerClass} shrink-0 whitespace-nowrap`}
          onSearch={() => setShowSearchModal(true)}
        />
        <button
          type="button"
          onClick={() => setShowSearchModal(true)}
          className={`${menuTriggerClass} min-[960px]:hidden`}
          aria-label="Search Duo Hub"
        >
          <HiMagnifyingGlass className="h-4 w-4" />
        </button>

        <div className="mx-3 hidden w-64 shrink-0 min-[960px]:flex xl:w-72 2xl:w-80">
          <button
            type="button"
            onClick={() => setShowSearchModal(true)}
            className="flex h-8 w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-50 py-0 pl-3 pr-1 text-sm font-medium text-gray-500 outline-none transition-colors hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-orange-500/40"
            aria-label="Search Duo Hub"
          >
            <HiMagnifyingGlass className="h-4 w-4 shrink-0" />
            <span>Search Duo Hub</span>
            <kbd className="ml-auto inline-flex h-6 min-w-10 items-center justify-center rounded-md border border-gray-200 bg-white px-2 font-sans text-[10px] font-medium leading-none text-gray-400">
              {/Mac|iPhone|iPad|iPod/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}
            </kbd>
          </button>
        </div>

        <div className="ml-auto flex min-w-0 items-center gap-2">
          {isOverride && (
            <div className="hidden h-8 items-center gap-1.5 rounded-md bg-orange-50 px-2 text-xs font-medium text-orange-800 ring-1 ring-inset ring-orange-200 sm:flex">
              <span className="max-w-32 truncate">
                Viewing as {userPermissions.Name || overrideEmail}
              </span>
              <button
                type="button"
                onClick={() => setShowAdminModal(true)}
                className="font-semibold hover:text-orange-950"
              >
                Change
              </button>
              <button
                type="button"
                onClick={clearOverride}
                className="font-semibold text-red-600 hover:text-red-700"
              >
                Exit
              </button>
            </div>
          )}
          <UserProfileDropdown
            userPermissions={userPermissions}
            isOpen={showProfileDropdown}
            onToggle={setShowProfileDropdown}
            enableAdminOverride={canUseViewAs}
            showSettings={showSettings}
            onViewAsUser={() => setShowAdminModal(true)}
            onClearOverride={onClearOverride}
            placement="bottom"
            showTriggerPicture={false}
          />
        </div>
      </div>

      {isModernHome &&
        homeNavigationHost &&
        createPortal(<HubLinkDirectory {...hubDirectoryProps} />, homeNavigationHost)}

      {/* Global Search Modal */}
      <GlobalSearchModal
        isOpen={showSearchModal}
        options={searchOptions}
        onClose={() => {
          setShowSearchModal(false)
          setSearchOptions({})
        }}
      />

      {/* Admin Override Modal */}
      <AdminOverrideModal
        isOpen={showAdminModal}
        onClose={() => setShowAdminModal(false)}
        onSelectUser={(email) => {
          if (onSelectUser) {
            onSelectUser(email)
          }
          setShowAdminModal(false)
        }}
      />
      <style nonce={cspNonce}>{`
        @keyframes navItemReveal {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .primary-nav-item-reveal {
          animation: navItemReveal 420ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .primary-nav-item-reveal { animation: none; }
          [data-top-bar] a, [data-top-bar] button { animation-duration: 1ms !important; animation-delay: 0ms !important; }
        }
        @keyframes fireGlowWobble {
          0% { transform: rotate(-3deg) scale(1); filter: drop-shadow(0 0 2px #ef4444); color: #ef4444; }
          50% { transform: rotate(3deg) scale(1.1); filter: drop-shadow(0 0 6px #f97316); color: #f97316; }
          100% { transform: rotate(-3deg) scale(1); filter: drop-shadow(0 0 2px #ef4444); color: #ef4444; }
        }
      `}</style>
    </header>
  )
}

export default TopBar
